import ActionRow from '@/components/ActionRow';
import CaptureList from '@/components/CaptureList';
import DialogModal from '@/components/DialogModal';
import StyledModal from '@/components/StyledModal';
import { refetchQueriesForArchive } from '@/pages/CategoryManager';
import { useDeleteBulkActions, useUpdateActions } from '@/services/action/hooks';
import { Action } from '@/types/actions';
import {
  getTotalDurationOfActions,
  getTotalDurationOfActionsInRange,
  getTotalDurationOfPreferredActions,
  getTotalDurationOfPreferredActionsInRange,
} from '@/utils/action';
import { validateRecurrence } from '@/utils/events';
import {
  Box,
  Button,
  Flex,
  ListItem,
  ModalBody,
  ModalFooter,
  Text,
  UnorderedList,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ItemsToComplete = {
  component: React.ReactNode;
}[];

export enum modeValues {
  delete = 'detele',
  canceled = 'canceled',
}

type CompleteModalProps = {
  isOpen: boolean;
  onClose: () => void;
  actions: Action[];
  mode?: modeValues;
  recurringDurationRange?: { start: Date; end: Date };
};

const DeleteBulkActionsModal = ({
  isOpen,
  onClose,
  actions,
  mode = modeValues.delete,
  recurringDurationRange,
}: CompleteModalProps) => {
  const { t } = useTranslation('DeleteBulkActionsModal');
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const [showConfirmMessage, setShowConfirmMessage] = useState(false);
  const [numberOfBlockToDelete, setNumberOfBlockToDelete] = useState<{ count: number; blockNames: string[] }>({
    count: 0,
    blockNames: [],
  });

  const category = actions[0].category;

  const [selectedActions, setSelectedActions] = useState<string[]>([]);

  const { mutate: deleteBulkActions } = useDeleteBulkActions({
    onSuccess: () => {
      refetchQueriesForArchive();
      setShowConfirmMessage(false);
      onClose();
      setIsLoading(false);
      toast({
        title: t('toasts.actionsDeleted'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const { mutate: updateBulkActions } = useUpdateActions({
    onSuccess: () => {
      refetchQueriesForArchive();
      setShowConfirmMessage(false);
      onClose();
      setIsLoading(false);
      toast({
        title: t('toasts.actionsCanceled'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const handleToggle = useCallback((actionId: string) => {
    setSelectedActions((prev) =>
      prev.includes(actionId) ? prev.filter((id) => id !== actionId) : [...prev, actionId],
    );
  }, []);

  const handleSelectAll = () => {
    const allActionIds = filteredActions.filter((a) => !a.isCanceled).map((a) => a.id);
    setSelectedActions(allActionIds);
  };

  const handleDeselectAll = () => {
    setSelectedActions([]);
  };

  const filteredActions = actions.filter(
    (a) => !a.category.isArchived && !a.project?.isArchived && a.progressStatus !== 'complete',
  );
  const filteredActionsToCompareInBlocks = actions.filter((a) => !a.category.isArchived && !a.project?.isArchived);

  const selectedRecurringActions = useMemo(
    () =>
      filteredActions.filter(
        (a) => selectedActions.includes(a.id) && validateRecurrence(a.event?.recurrence),
      ),
    [filteredActions, selectedActions],
  );

  const bulkCancelIncludesRecurring = mode === modeValues.canceled && selectedRecurringActions.length > 0;

  const countBlocksToDelete = (actions: Action[], selectedActionIds: string[]) => {
    const actionsWithBlock = actions.filter((action) => action.blockId !== null && action.blockId !== undefined);

    const blocks = actionsWithBlock.reduce(
      (acc: any, action) => {
        const blockId = action.blockId;
        if (!blockId) return;
        if (!acc[blockId]) acc[blockId] = [];
        acc[blockId].push(action);
        return acc;
      },
      {} as Record<string, Action[]>,
    );

    let blocksToDelete = 0;
    let blockNamesToDelete: string[] = [];

    Object.entries(blocks).forEach(([, actions]: any) => {
      const allSelected = actions.every((action: Action) => selectedActionIds.includes(action.id));

      if (allSelected) {
        blocksToDelete++;
        const blockName = actions[0].block.result;
        if (blockName) {
          blockNamesToDelete.push(blockName);
        }
      }
    });

    return {
      count: blocksToDelete,
      blockNames: blockNamesToDelete,
    };
  };

  const openDialogModal = () => {
    if (mode === modeValues.delete)
      setNumberOfBlockToDelete(countBlocksToDelete(filteredActionsToCompareInBlocks, selectedActions));
    setShowConfirmMessage(true);
  };

  const handleDeleteActions = () => {
    if (mode === modeValues.canceled) {
      updateBulkActions(
        selectedActions.map((id) => ({
          _set: { isCanceled: true },
          where: { id: { _eq: id } },
        })),
      );
      setIsLoading(true);
    } else {
      deleteBulkActions({
        ids: selectedActions,
      });
      setIsLoading(true);
    }
  };

  const isSelectAllDisabled = useMemo(() => {
    return (
      filteredActions.length === 0 ||
      filteredActions.every(
        (a) => a.isCanceled || selectedActions.length === filteredActions.filter((a) => !a.isCanceled).length,
      )
    );
  }, [filteredActions, selectedActions]);

  const selectAllbutonHidden = useMemo(() => {
    return filteredActions.every((a) => a.isCanceled);
  }, [filteredActions, selectedActions]);

  const itemsToComplete = useMemo(() => {
    const items: ItemsToComplete = [];
    items.push({
      component: (
        <CaptureList
          key={category.id}
          category={category}
          totalDurationOfIncompleteActions={
            recurringDurationRange
              ? getTotalDurationOfActionsInRange(
                  filteredActions,
                  recurringDurationRange.start,
                  recurringDurationRange.end,
                )
              : getTotalDurationOfActions(filteredActions)
          }
          totalDurationOfPreferredIncompleteActions={
            recurringDurationRange
              ? getTotalDurationOfPreferredActionsInRange(
                  filteredActions,
                  recurringDurationRange.start,
                  recurringDurationRange.end,
                )
              : getTotalDurationOfPreferredActions(filteredActions)
          }
          hideAccordion
          handleSelectAll={handleSelectAll}
          handleDeselectAll={handleDeselectAll}
          selectButtonProps={{
            isHidden: selectAllbutonHidden,
            selectAllDisabled: isSelectAllDisabled,
          }}
        >
          <VStack>
            {filteredActions.map((action) => (
              <ActionRow
                key={action.id}
                action={action}
                isDeleteMode
                isSelected={selectedActions.includes(action.id)}
                onToggle={() => handleToggle(action.id)}
              />
            ))}
          </VStack>
        </CaptureList>
      ),
    });
    return items;
  }, [
    category,
    filteredActions,
    selectedActions,
    handleToggle,
    recurringDurationRange,
    isSelectAllDisabled,
    selectAllbutonHidden,
    handleSelectAll,
    handleDeselectAll,
  ]);

  const itemActive = itemsToComplete[0];

  const onCloseModal = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <StyledModal contentProps={{ maxHeight: '87vh' }} isOpen={isOpen} onClose={onCloseModal} size="4xl">
      <>
        <ModalBody overflow="auto">
          <Flex justifyContent="space-between" marginTop={2} marginBottom={4}>
            <Text width="100%" fontSize="large" fontWeight={900} textAlign="left">
              {mode === modeValues.canceled ? t('title.confirm') : t('title.deleteActions')}
            </Text>
          </Flex>
          <Box>{itemActive?.component}</Box>
        </ModalBody>
        <ModalFooter>
          <>
            <Button onClick={onCloseModal} size="lg" variant="secondary">
              {t('buttons.cancel')}
            </Button>

            <Button
              color={mode === modeValues.canceled ? 'white.1000' : 'white.1000'}
              isDisabled={selectedActions.length === 0}
              isLoading={isLoading}
              onClick={openDialogModal}
              size="lg"
              variant={mode === modeValues.canceled ? 'primary' : 'danger'}
            >
              {mode === modeValues.canceled ? t('buttons.canceledSelectedActions') : t('buttons.deleteSelectedActions')}
            </Button>
          </>
        </ModalFooter>
        {showConfirmMessage && (
          <DialogModal
            isOpen={showConfirmMessage}
            onCancel={() => setShowConfirmMessage(false)}
            message={
              bulkCancelIncludesRecurring
                ? t('messages.confirmCancelRecurring')
                : mode === modeValues.canceled
                  ? t(selectedActions.length > 1 ? 'messages.confirmPluralCancel' : 'messages.confirmSingularCancel')
                  : t(selectedActions.length > 1 ? 'messages.confirmPlural' : 'messages.confirmSingular')
            }
            title={
              bulkCancelIncludesRecurring ? t('title.cancelRecurring') : t('title.confirm')
            }
            onConfirm={handleDeleteActions}
            confirmText={
              bulkCancelIncludesRecurring ? t('buttons.confirmCancel') : t('buttons.confirm')
            }
            confirmVariant={mode === modeValues.canceled ? 'primary' : 'danger'}
            messageSize="large"
          >
            <Box>
              {numberOfBlockToDelete?.count > 0 && (mode === modeValues.canceled || mode === modeValues.delete) && (
                <Box>
                  <Text
                    color={mode === modeValues.canceled ? 'text-secondary' : undefined}
                    fontSize="large"
                    fontWeight={500}
                  >
                    {mode === modeValues.canceled
                      ? t('messages.hideBlockInfo')
                      : t(
                          numberOfBlockToDelete.count === 1
                            ? 'messages.deleteSingleBlock'
                            : 'messages.deleteMultipleBlocks',
                          { count: numberOfBlockToDelete.count },
                        )}
                  </Text>
                  {mode === modeValues.canceled && (
                    <Text marginTop={2} fontSize="large" fontWeight={500}>
                      {t('messages.hideBlockAffects', { count: numberOfBlockToDelete.count })}
                    </Text>
                  )}
                  <UnorderedList marginTop={2}>
                    {numberOfBlockToDelete.blockNames.map((blockName, index) => (
                      <ListItem key={index} fontWeight="bold">
                        • {blockName}
                      </ListItem>
                    ))}
                  </UnorderedList>
                </Box>
              )}
            </Box>
          </DialogModal>
        )}
      </>
    </StyledModal>
  );
};

export default DeleteBulkActionsModal;
